/**
 * The shape of `GET /api/summary`, and how its numbers are worded.
 *
 * The arithmetic lives in the API (`routes/aircraft.ts`) and is NOT repeated here —
 * two surfaces (Dashboard and the co-owner page) render this summary and they must
 * never be able to disagree. What lives here is the wording.
 *
 * There is no meter in it: the Hobbs balance went with the engine basis. Flight
 * time is summed where it has been entered, and the legs without one are counted
 * beside the sum, never read as zero.
 */

export interface AircraftSummary {
  my_flights: number;
  my_hours: number;
  my_flights_without_time: number;
  my_distance_nm: number;
  my_local_flights: number;
  my_unknown_distance_flights: number;
  other_flights: number;
  other_hours: number;
  other_flights_without_time: number;
  unlogged_flights: number;
}

export interface UnloggedView {
  /** Zero is the answer he is after, so it gets a state of its own — never hidden. */
  state: 'clear' | 'pending';
  value: number;
  label: string;
  accent: string;
  hint: string;
}

/**
 * Detections still awaiting a Log it / Other owner / Ignore decision.
 *
 * Zero reads as reassurance rather than an absent row: "every flight FlightAware
 * saw has been dealt with" is the whole point of looking.
 */
export function unloggedView(count: number | null | undefined): UnloggedView {
  const n = count ?? 0;
  if (n <= 0) {
    return {
      state: 'clear',
      value: 0,
      label: 'Unlogged',
      accent: 'text-emerald-300',
      hint: 'every detected flight is classified',
    };
  }
  return {
    state: 'pending',
    value: n,
    label: 'Unlogged',
    accent: 'text-amber-300',
    hint: `${n === 1 ? 'flight' : 'flights'} FlightAware saw, not yet classified`,
  };
}

/** "3 legs have no flight time yet" — or nothing, when every leg has one. */
export function missingTimeNote(missing: number | null | undefined): string | null {
  const n = missing ?? 0;
  if (n <= 0) return null;
  return `${n} ${n === 1 ? 'leg has' : 'legs have'} no flight time yet, and ${n === 1 ? 'is' : 'are'} not in this total`;
}

/** Hours to one decimal, with how many legs are outside the sum. */
export function hoursHint(flights: number, missing: number): string {
  const legs = `${flights} ${flights === 1 ? 'flight' : 'flights'}`;
  return missing > 0 ? `${legs} · ${missing} without a flight time` : legs;
}

/** A leg's distance as a page shows it: nautical miles, "local", or "unknown" — never 0. */
export function distanceLabel(leg: { distance_nm: number | null; distance_kind: string } | null | undefined): string {
  if (!leg) return '—';
  if (leg.distance_kind === 'local') return 'local';
  if (leg.distance_nm == null) return 'unknown';
  return `${leg.distance_nm.toLocaleString()} nm`;
}

/** Where a leg went, for a subtitle: "McGhee Tyson Airport, Knoxville/Maryville". */
export function placeLabel(airport: { name: string; city: string } | null | undefined): string | null {
  if (!airport) return null;
  return airport.city ? `${airport.name}, ${airport.city}` : airport.name;
}

/** An airport as the API names it (`services/distance.ts`). */
export interface Airport {
  code: string;
  name: string;
  city: string;
}

/** Destination and distance, computed by the API from a leg's two codes. */
export interface RouteFacts {
  departure: Airport | null;
  destination: Airport | null;
  distance_nm: number | null;
  distance_kind: 'great_circle' | 'local' | 'unknown';
}
